import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const CartContext = createContext(null);
export const WHATSAPP_NUMBER =
    process.env.REACT_APP_WHATSAPP_NUMBER || "919100005390";

const KEY = "kp_cart_v2";

export const WEIGHT_GRAMS = { "250g": 250, "500g": 500, "1kg": 1000 };
export const FREE_SHIPPING_MIN_GRAMS = 1000;

const loadInitial = () => {
    if (typeof window === "undefined") return [];
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(loadInitial);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(KEY, JSON.stringify(items));
        } catch {}
    }, [items]);

    // key = productId + weight to allow multiple sizes of the same product
    const makeKey = (productId, weight) => `${productId}__${weight}`;

    const add = useCallback((product, size) => {
        const chosen = size || (product.sizes && product.sizes[0]) || {
            weight: product.weight || "250g",
            price: product.price || 0,
        };
        const key = makeKey(product.id, chosen.weight);
        setItems((prev) => {
            const existing = prev.find((i) => i.key === key);
            if (existing) {
                return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
            }
            return [
                ...prev,
                {
                    key,
                    id: product.id,
                    name: product.name,
                    image: product.image,
                    weight: chosen.weight,
                    price: chosen.price,
                    qty: 1,
                },
            ];
        });
    }, []);

    const remove = useCallback((key) => {
        setItems((prev) => prev.filter((i) => i.key !== key));
    }, []);

    const update = useCallback((key, qty) => {
        setItems((prev) =>
            prev
                .map((i) => (i.key === key ? { ...i, qty: Math.max(0, qty) } : i))
                .filter((i) => i.qty > 0),
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    /**
     * Merge a batch of items into the cart (used by "Reorder" on the /orders page).
     * Input items have shape { id, name (bilingual obj), image, weight, price, qty }.
     * Existing lines of the same product+weight are topped-up by the new qty.
     */
    const mergeItems = useCallback((incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0) return;
        setItems((prev) => {
            const byKey = new Map(prev.map((i) => [i.key, { ...i }]));
            incoming.forEach((it) => {
                const key = makeKey(it.id, it.weight);
                if (byKey.has(key)) {
                    const cur = byKey.get(key);
                    byKey.set(key, { ...cur, qty: cur.qty + Math.max(1, it.qty || 1) });
                } else {
                    byKey.set(key, {
                        key,
                        id: it.id,
                        name: it.name,
                        image: it.image,
                        weight: it.weight,
                        price: it.price,
                        qty: Math.max(1, it.qty || 1),
                    });
                }
            });
            return Array.from(byKey.values());
        });
    }, []);

    const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
    const subtotal = useMemo(
        () => items.reduce((s, i) => s + i.price * i.qty, 0),
        [items],
    );

    // Packed weight of the basket — free delivery in AP/Telangana needs 1 kg or more.
    const weightGrams = useMemo(
        () => items.reduce((s, i) => s + (WEIGHT_GRAMS[i.weight] || 0) * i.qty, 0),
        [items],
    );

    return (
        <CartContext.Provider
            value={{ items, add, remove, update, clear, mergeItems, count, subtotal, weightGrams, open, setOpen }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};

export const buildWhatsAppUrl = (message) => {
    const encoded = encodeURIComponent(message);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
};
